import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, firstValueFrom } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface User {
  user_id: string;
  name: string;
  full_name?: string;
  email: string;
  phone_number?: string;
  city?: string;
  state?: string;
  country?: string;
  full_address?: string;
  role_id: number; // 1=user, 2=admin, 3=super_admin, 4=backoffice
}

export interface SignUpRequest {
  full_name: string;
  phone_number: string;
  email: string;
  password: string;
  city: string;
  state: string;
  country: string;
  pin_code?: string;
  full_address: string;
}

export interface LoginRequest {
  phone_number: string;
  password: string;
  recaptcha_token: string;
}

export interface LoginResponse {
  message: string;
  user?: {
    user_id: string;
    full_name: string;
    email: string;
    phone_number: string;
    role_id?: number | string;
    role?: number | string;
    role_name?: string;
  };
  role_id?: number | string;
  role?: number | string;
  role_name?: string;
}

export interface SignUpResponse {
  message: string;
  user_id?: number;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface ResetPasswordResponse {
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = environment.apiUrl;
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private userSubject = new BehaviorSubject<User | null>(null);

  isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  user$ = this.userSubject.asObservable();

  constructor(private http: HttpClient) {
    // Auth state will be initialized by APP_INITIALIZER
  }

  // Public method for APP_INITIALIZER
  async initializeAuthState(): Promise<void> {
    return this.checkAuthState();
  }

  private async checkAuthState(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<User>(`${this.apiUrl}/auth/me`, { withCredentials: true })
      );
      
      const user: User = {
        user_id: response.user_id,
        name: response.full_name || response.name,
        email: response.email,
        phone_number: response.phone_number,
        city: response.city,
        state: response.state,
        country: response.country,
        full_address: response.full_address,
        role_id: response.role_id
      };
      
      this.userSubject.next(user);
      this.isAuthenticatedSubject.next(true);
    } catch (error) {
      // No valid cookie or user not authenticated
      this.userSubject.next(null);
      this.isAuthenticatedSubject.next(false);
    }
  }

  get isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  get user(): User | null {
    return this.userSubject.value;
  }

  async sendOtp(phoneNumber: string): Promise<string> {
    try {
      const response = await firstValueFrom(
        this.http.post<{ message: string; expires_in_minutes?: number }>(
          `${this.apiUrl}/auth/send-otp`,
          { phone_number: phoneNumber }
        )
      );
      return response.message || 'OTP sent successfully';
    } catch (error: any) {
      throw new Error(error.error?.detail || 'Failed to send OTP');
    }
  }

  async verifyOtp(phoneNumber: string, otp: string): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.post<LoginResponse>(
          `${this.apiUrl}/auth/verify-otp`,
          { phone_number: phoneNumber, otp: otp },
          { withCredentials: true }
        )
      );

      if (!response.user) {
        throw new Error('OTP verification did not include user data');
      }

      const user: User = {
        user_id: response.user?.user_id || '',
        name: response.user?.full_name || phoneNumber,
        email: response.user?.email || '',
        phone_number: phoneNumber,
        city: (response.user as any)?.city,
        state: (response.user as any)?.state,
        country: (response.user as any)?.country,
        full_address: (response.user as any)?.full_address,
        role_id: this.normalizeRoleId(response)
      };

      this.userSubject.next(user);
      this.isAuthenticatedSubject.next(true);
    } catch (error: any) {
      this.userSubject.next(null);
      this.isAuthenticatedSubject.next(false);
      throw new Error(error.error?.detail || error.message || 'OTP verification failed');
    }
  }

  async signUp(data: SignUpRequest): Promise<SignUpResponse> {
    try {
      const response = await firstValueFrom(
        this.http.post<SignUpResponse>(`${this.apiUrl}/auth/signup`, data)
      );
      return response;
    } catch (error: any) {
      throw new Error(error.error?.detail || 'Sign up failed');
    }
  }

  async login(phoneNumber: string, password: string, recaptchaToken: string): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.post<LoginResponse>(`${this.apiUrl}/auth/login`, {
          phone_number: phoneNumber,
          password: password,
          recaptcha_token: recaptchaToken
        }, { withCredentials: true }) // Enable sending/receiving cookies
      );

      if (!response.user) {
        throw new Error('Login response did not include user data');
      }

      const user: User = {
        user_id: response.user?.user_id || '',
        name: response.user?.full_name || phoneNumber,
        email: response.user?.email || '',
        phone_number: phoneNumber,
        city: (response.user as any)?.city,
        state: (response.user as any)?.state,
        country: (response.user as any)?.country,
        full_address: (response.user as any)?.full_address,
        role_id: this.normalizeRoleId(response)
      };

      // Update subjects (no storage needed - cookie handles auth)
      this.userSubject.next(user);
      this.isAuthenticatedSubject.next(true);
    } catch (error: any) {
      // Clear any partial state
      this.userSubject.next(null);
      this.isAuthenticatedSubject.next(false);
      throw new Error(error.error?.detail || error.message || 'Login failed');
    }
  }

  async forgotPassword(email: string): Promise<string> {
    try {
      const response = await firstValueFrom(
        this.http.post<ForgotPasswordResponse>(`${this.apiUrl}/auth/forgot`, { email })
      );
      return response.message || 'If this email is registered, a password reset link has been sent.';
    } catch (error: any) {
      throw new Error(error.error?.detail || 'Failed to send reset link');
    }
  }

  async resetPassword(token: string, newPassword: string): Promise<string> {
    try {
      const response = await firstValueFrom(
        this.http.post<ResetPasswordResponse>(`${this.apiUrl}/auth/reset-password`, {
          token,
          new_password: newPassword
        })
      );
      return response.message || 'Password reset successful';
    } catch (error: any) {
      throw new Error(error.error?.detail || 'Password reset failed');
    }
  }

  // Helper methods for role checking
  isAdmin(): boolean {
    const user = this.userSubject.value;
    return user?.role_id === 2 || user?.role_id === 3;
  }

  isOnlyAdmin(): boolean {
    const user = this.userSubject.value;
    return user?.role_id === 2;
  }

  isSuperAdmin(): boolean {
    const user = this.userSubject.value;
    return user?.role_id === 3;
  }

  isBackOffice(): boolean {
    const user = this.userSubject.value;
    return user?.role_id === 4;
  }

  getUserRole(): string {
    const user = this.userSubject.value;
    if (!user) return 'guest';
    if (user.role_id === 4) return 'backoffice';
    if (user.role_id === 3) return 'super_admin';
    if (user.role_id === 2) return 'admin';
    return 'user';
  }

  private normalizeRoleId(source: any): number {
    const roleCandidate =
      source?.role_id ??
      source?.role ??
      source?.role_name ??
      source?.user?.role_id ??
      source?.user?.role ??
      source?.user?.role_name;

    if (typeof roleCandidate === 'number' && Number.isFinite(roleCandidate)) {
      return roleCandidate;
    }

    if (typeof roleCandidate === 'string') {
      const role = roleCandidate.trim().toLowerCase();
      if (role === '4' || role === 'backoffice' || role === 'back_office' || role === 'back-office') return 4;
      if (role === '3' || role === 'super_admin' || role === 'super-admin' || role === 'superadmin') return 3;
      if (role === '2' || role === 'admin') return 2;
      if (role === '1' || role === 'user') return 1;
      const asNumber = Number(role);
      if (!Number.isNaN(asNumber)) return asNumber;
    }

    return 1;
  }

  /**
   * Attempt to refresh the auth token/session via the backend.
   * Returns Observable<boolean> — true if refresh succeeded, false otherwise.
   */
  refreshToken(): Observable<boolean> {
    return this.http.post<{ message?: string }>(
      `${this.apiUrl}/auth/refresh`, {}, { withCredentials: true }
    ).pipe(
      map(() => {
        // Refresh succeeded — re-fetch user state
        this.checkAuthState();
        return true;
      }),
      catchError(() => {
        return of(false);
      })
    );
  }

  logout(): void {
    // Call backend to clear cookie
    this.http.post(`${this.apiUrl}/auth/logout`, {}, { withCredentials: true })
      .subscribe({
        error: (err) => console.error('Logout error:', err)
      });
    
    // Clear local state
    this.userSubject.next(null);
    this.isAuthenticatedSubject.next(false);
  }
}
